import { Component, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { Subscription } from 'rxjs';

import { alertService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { TaxIdValidators } from '@unpispas/upp-defs';

import { Client } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { Place } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { TicketInvoice } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { ViewRenderer } from '@unpispas/upp-wdgt';

import { UppClientChanged } from '@unpispas/feature/client';
import { printService } from '@unpispas/feature/print';
import { queueService } from '@unpispas/feature/ticket';
import { UppTicketPrintComponent } from '@unpispas/feature/ticket';

/** Guest receipt panel (home panel `RECEIPT`). */
@Component({
    selector: 'upp-guest-receipt',
    templateUrl: './upp-receipt.html',
    styleUrls: ['./upp-receipt.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestReceiptComponent extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input() qrcode: QrCodeView | null = null;
    @Output() Close = new EventEmitter<void>();

    @ViewChild('printable') printable?: UppTicketPrintComponent;

    /********************************/
    /* Subscriptions                */
    /********************************/

    private _qrcode_subscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
        private printer: printService,
        private queue: queueService,
        private alertCtrl: alertService,
        private toast: toastService,
        public view: viewService,
    ) {
        super(lang, change.cdref);
    }

    /********************************/
    /* Tickets                      */
    /********************************/

    get Tickets(): Array<TicketView> {
        return this.qrcode?.PaidTickets ?? [];
    }

    get place(): PlaceView | null {
        return this.qrcode?.place ?? null;
    }

    private _selected: TicketView | null = null;
    get Selected(): TicketView | null {
        if (this.Tickets.length === 1) {
            return this.Tickets[0];
        }
        return this._selected;
    }

    TicketTrack(_index: number, ticket: TicketView): string | null {
        return ticket?._uuid || null;
    }

    /********************************/
    /* Invoice request              */
    /********************************/

    private _invoiceMode: 'create' | 'modify' | null = null;
    private _invoicing = false;
    private _proceedable = false;
    private _taxIdUnverified = false;
    private _sendInvoiceEmail = false;
    private _client: Client | null = null;

    get InvoiceForm(): boolean {
        return this._invoiceMode !== null;
    }

    get IsModifyingInvoice(): boolean {
        return this._invoiceMode === 'modify';
    }

    get InvoiceConfirmLabel(): string {
        return this._invoiceMode === 'modify'
            ? '@place_ticket_set_invoice'
            : '@place_ticket_do_invoice';
    }

    get CanRequestInvoice(): boolean {
        return !!this.Selected
            && this.Selected.invoice == null
            && !this.Selected.invceac;
    }

    get CanModifyInvoice(): boolean {
        return !!this.Selected
            && this.Selected.invoice != null
            && !this.Selected.invceac;
    }

    get CanCreateInvoice(): boolean {
        return this._proceedable && !this._invoicing;
    }

    get CanOfferSendInvoice(): boolean {
        return this._invoiceMode !== null;
    }

    get CanEnableSendInvoice(): boolean {
        if (!this._proceedable) {
            return false;
        }

        return this._HasValidEmail(String(this.InvoiceClient.email || '').trim());
    }

    get SendInvoiceEmail(): boolean {
        return this._sendInvoiceEmail;
    }

    get InvoiceClient(): Client {
        if (this._client === null) {
            this._client = new Client(null, this.data);
            if (this._invoiceMode === 'modify') {
                const _linked = this.Selected?.invoice?.client?.last
                    ?? this.Selected?.invoice?.client;
                if (_linked) {
                    this._client.name = _linked.name;
                    this._client.taxid = _linked.taxid;
                    this._client.email = _linked.email;
                    this._client.address = _linked.address;
                    this._client.postal = _linked.postal;
                    this._client.region = _linked.region;
                    this._client.country = _linked.country;
                }
            }
        }

        return this._client;
    }

    /********************************/
    /* Lifecycle                    */
    /********************************/

    ngOnInit(): void {
        this._BindQrcode();
    }

    ngOnChanges(): void {
        this._BindQrcode();
    }

    ngOnDestroy(): void {
        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        super.OnDestroy();
    }

    private _BindQrcode(): void {
        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        if (this.qrcode) {
            this._qrcode_subscription = this.qrcode.OnRefresh.subscribe(
            () => {
                if (this._selected && !this.Tickets.includes(this._selected)) {
                    this._selected = null;
                    this._ResetInvoiceForm();
                }
                this.change.markForCheck();
            });
        }
    }

    /********************************/
    /* Actions                      */
    /********************************/

    OnClose(): void {
        this.Close.emit();
    }

    OnSelectTicket(ticket: TicketView | null): void {
        this._selected = ticket;
        this._ResetInvoiceForm();
        if ((this._selected === null) && (this.Tickets.length === 1)) {
            this.OnClose();
        }
        this.change.markForCheck();
    }

    async OnDownloadPDF(): Promise<void> {
        if (!this.printable || !this.Selected) {
            return;
        }

        try {
            const _element = await this.printable.Printable();
            if (!_element) {
                return;
            }

            const _blob = await this.printer.TicketToPdf(_element);
            this._TriggerDownload(_blob, this.Selected);
        }
        finally {
            this.printable.IsPrinting = false;
        }
    }

    OnRequestInvoice(): void {
        if (!this.CanRequestInvoice) {
            return;
        }

        this._OpenInvoiceForm('create');
    }

    OnModifyInvoice(): void {
        if (!this.CanModifyInvoice) {
            return;
        }

        this._OpenInvoiceForm('modify');
    }

    OnCancelInvoice(): void {
        this._ResetInvoiceForm();
        this.change.markForCheck();
    }

    OnClientChange(ev: UppClientChanged): void {
        this._proceedable = ev.invoiceProceedable;
        this._taxIdUnverified = ev.taxIdUnverified;
        if (!this.CanEnableSendInvoice) {
            this._sendInvoiceEmail = false;
        }
        this.change.markForCheck();
    }

    OnSendInvoiceEmailChange(ev: CustomEvent<{ checked?: boolean }>): void {
        if (!this.CanEnableSendInvoice) {
            this._sendInvoiceEmail = false;
            this.change.markForCheck();
            return;
        }

        this._sendInvoiceEmail = !!ev.detail?.checked;
        this.change.markForCheck();
    }

    async OnCreateInvoice(): Promise<void> {
        if (this._invoicing || !this.CanCreateInvoice || !this.Selected) {
            return;
        }

        if (this.Selected.invceac) {
            return;
        }

        this._invoicing = true;

        try {
            if (this.Selected?.ZeroRateInvoice && !TaxIdValidators.IsZeroRate(this.InvoiceClient?.taxid, this.InvoiceClient?.country)) {
                const _alert = this.alertCtrl.alert({
                    header: this.lang.tr('@place_ticket_invoice_zerorate_title'),
                    message: this.lang.tr('@place_ticket_invoice_zerorate_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss(false);
                            }, 100);
                            return false;
                        },
                    }],
                });

                await _alert.present();
                await _alert.onDidDismiss();
                return;
            }

            if (this._taxIdUnverified) {
                const _confirmed = await this._ConfirmUnsafeTaxId();
                if (!_confirmed) {
                    return;
                }
            }

            const _ticket = this.Selected;
            const _place = _ticket.place?.object ?? this.place?.object;
            if (!_place) {
                return;
            }

            const _client = this._CanonicalClient(_place);

            const _invoice = new TicketInvoice(null, this.data);
            _invoice.ticket = _ticket.object;
            _invoice.status = 'AC';
            _invoice.client = _client;
            _ticket.invoice = _invoice;

            const _wantEmail = this._sendInvoiceEmail && this._HasValidEmail(String(_client.email || '').trim());
            const _email = String(_client.email || '').trim();

            void this.toast.ShowWait('@place_receipt_preparing');
            try {
                await _ticket.OnCommit(null);
                this._ResetInvoiceForm();
                if (_wantEmail) {
                    await this._SendInvoiceAfterCreate(_ticket, _email);
                }
            }
            finally {
                this.toast.HideWait();
            }
        }
        finally {
            this._invoicing = false;
            this.change.markForCheck();
        }
    }

    private async _SendInvoiceAfterCreate(ticket: TicketView, email: string): Promise<void> {
        let _prepared: { element: HTMLElement; release: () => void } | null = null;
        try {
            _prepared = await this.queue.PrepareReceiptElement(ticket);
            if (!_prepared) {
                console.error('[GUEST RECEIPT] Cannot send invoice email: PrepareReceiptElement returned null');
                this.toast.ShowAlert('danger', this.tr('@service_request_error_1'));
                return;
            }

            const _series = String(ticket.series || '');
            const _number = String(ticket.invceno ?? '');
            const _invoiceName = (_series || _number)
                ? (_series + _number + '.pdf')
                : 'invoice.pdf';

            const _code = await this.printer.SendInvoiceEmail({
                element: _prepared.element,
                lang: this.lang.GetLanguage().toLowerCase(),
                recipient: email,
                invoiceName: _invoiceName,
                ticketObjid: ticket.objid,
                guest: true,
            });

            if (_code === 0) {
                this.toast.ShowAlert('primary', this.tr('@place_ticket_sent_message', [ email ]), 3000);
            }
            else {
                console.error('[GUEST RECEIPT] Invoice email send failed with errorcode', _code);
                this.toast.ShowAlert('danger', this.tr('@service_request_error_' + _code));
            }
        }
        catch (err) {
            console.error('[GUEST RECEIPT] Invoice email send threw', err);
            this.toast.ShowAlert('danger', this.tr('@service_request_error_1'));
        }
        finally {
            _prepared?.release();
        }
    }

    private _OpenInvoiceForm(mode: 'create' | 'modify'): void {
        this._client = null;
        this._proceedable = false;
        this._taxIdUnverified = false;
        this._sendInvoiceEmail = false;
        this._invoiceMode = mode;

        void this.InvoiceClient;
        this.change.markForCheck();
    }

    private _ResetInvoiceForm(): void {
        this._invoiceMode = null;
        this._proceedable = false;
        this._taxIdUnverified = false;
        this._sendInvoiceEmail = false;
        this._client = null;
    }

    private _HasValidEmail(email: string): boolean {
        if (!email) {
            return false;
        }

        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    private _CanonicalClient(place: Place): Client {
        const _form = this.InvoiceClient;
        _form.place = place;
        _form.name = String(_form.name || '').trim() || null;
        _form.taxid = String(_form.taxid ?? '').trim() || null;
        _form.email = String(_form.email || '').trim() || null;
        _form.address = String(_form.address || '').trim() || null;
        _form.postal = String(_form.postal || '').trim() || null;
        _form.region = String(_form.region || '').trim() || null;
        _form.country = String(_form.country || '').trim().toUpperCase() || null;
        _form.status = 'AC';
        _form.invoiced = new Date();
        if (!_form.next?.IsValid) {
            _form.next = null;
        }

        place.AddClient(_form);
        return _form;
    }

    private async _ConfirmUnsafeTaxId(): Promise<boolean> {
        const _alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_ticket_invoice_unsafe_taxid_title'),
            message: this.lang.tr('@place_ticket_invoice_unsafe_taxid_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        _alert.dismiss(true);
                    }, 100);
                    return false;
                },
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        _alert.dismiss(false);
                    }, 100);
                    return false;
                },
            }],
        });

        await _alert.present();
        const _detail = await _alert.onDidDismiss();
        return _detail.data === true;
    }

    private _TriggerDownload(blob: Blob, ticket: TicketView): void {
        const _link = document.createElement('a');
        _link.href = window.URL.createObjectURL(blob);
        _link.download = 'ticket - ' + ticket.orderno + '.pdf';
        document.body.appendChild(_link);
        _link.click();
        document.body.removeChild(_link);
        window.URL.revokeObjectURL(_link.href);
    }
}
